/**
 * Krok UX2 — AKTUÁLNOST V SEZNAMU ZAKÁZEK.
 *
 * Zadání, sekce Aktuálnost: „**Nedělat hromadné přeprověření automaticky**
 * (stojí peníze); v seznamu zakázek jen filtr „prověřeno před více než
 * 14 dny“ a hromadná akce s potvrzením a odhadem počtu.“
 *
 * Tenhle modul je ČISTÝ výpočet nad odpovědí `GET /v1/audit-runs/summary`.
 * Odhad počtu v potvrzení a seznam, který se opravdu spustí, vznikají
 * ze STEJNÉ funkce — číslo v dialogu se tak nemůže rozejít s tím, co se
 * pak zaplatí.
 *
 * Hranice je stejná jako na kokpitu (`STALE_AFTER_DAYS`), aby detail
 * a seznam neříkaly o jedné zakázce dvě různé věci.
 */

import { STALE_AFTER_DAYS } from './case-cockpit.js';
import { systemClock } from '@reos/config';

/** Řádek odpovědi `GET /v1/audit-runs/summary` (`AuditRunSummaryItem`). */
export interface CaseFreshnessRow {
  /** Zakázka (`cases.id`). */
  readonly dealId: string;
  /** Kdy naposledy doběhl úspěšný běh; `null` = nikdy. */
  readonly lastFinishedAt: string | null;
  /** Běh je ve frontě nebo právě běží. */
  readonly running: boolean;
  readonly runCount: number;
}

export interface CaseFreshnessInfo {
  readonly checkedAt: string;
  readonly ageDays: number;
  readonly stale: boolean;
}

/** Stáří posledního doběhlého běhu; `null` = zakázku nikdo neprověřoval. */
export function freshnessOf(
  row: CaseFreshnessRow,
  now: Date = systemClock.now(),
): CaseFreshnessInfo | null {
  if (row.lastFinishedAt === null) return null;
  const ageDays = Math.max(
    0,
    Math.floor((now.getTime() - new Date(row.lastFinishedAt).getTime()) / 86_400_000),
  );
  return { checkedAt: row.lastFinishedAt, ageDays, stale: ageDays > STALE_AFTER_DAYS };
}

/**
 * Patří zakázka do filtru „prověřeno před více než 14 dny“?
 *
 * Dvě rozhodnutí, obě ve prospěch makléře:
 *  - zakázka, kterou NIKDO neprověřoval, ve filtru **je** — jinak by z něj
 *    vypadly právě ty nejhorší případy,
 *  - zakázka s právě běžícím během ve filtru **není** — už se na ní pracuje
 *    a druhý běh by jen stál peníze podruhé.
 */
export function isStaleCase(row: CaseFreshnessRow, now: Date = systemClock.now()): boolean {
  if (row.running) return false;
  const freshness = freshnessOf(row, now);
  return freshness === null || freshness.stale;
}

export interface BulkRecheckEstimate {
  /** Zakázky, které se opravdu spustí — v tomhle pořadí. */
  readonly dealIds: readonly string[];
  /** Kolik běhů se zaplatí. Tohle číslo jde do potvrzovacího dialogu. */
  readonly count: number;
  /** Kolik zakázek se vynechalo, protože na nich běh už běží. */
  readonly skippedRunning: number;
}

/**
 * Odhad pro potvrzovací dialog hromadného přeprověření. Vrací i konkrétní
 * seznam, aby tlačítko spustilo PŘESNĚ to, co dialog spočítal.
 */
export function bulkRecheckEstimate(
  rows: readonly CaseFreshnessRow[],
  now: Date = systemClock.now(),
): BulkRecheckEstimate {
  const dealIds = rows.filter((row) => isStaleCase(row, now)).map((row) => row.dealId);
  // Vynechane = ty, ktere by ve filtru byly, NEBYT beziciho behu.
  const skippedRunning = rows.filter(
    (row) => row.running && isStaleCase({ ...row, running: false }, now),
  ).length;
  return { dealIds, count: dealIds.length, skippedRunning };
}
